"use client";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import type { CSSProperties } from "react";

interface ShareCardProps {
  width: number;
  height: number;
  score: number;
  url: string;
  createdAt: string | Date;
  pageSpeedScore?: number;
  websiteQualityScore?: number;
  trustSecurityScore?: number;
  reportId?: string;
  className?: string;
  style?: CSSProperties;
}

export function ShareCard({
  width,
  height,
  score,
  url,
  createdAt,
  pageSpeedScore,
  websiteQualityScore,
  trustSecurityScore,
  reportId,
  className = "",
  style = {},
}: ShareCardProps) {
  // const formattedDate = (() => {
  //   try {
  //     const date = new Date(createdAt);
  //     return date.toLocaleDateString(undefined, {
  //       year: "numeric",
  //       month: "short",
  //       day: "2-digit",
  //     });
  //   } catch {
  //     return "";
  //   }
  // })();

  // Extract hostname for favicon and clean URL for display
  let hostname = "";
  let displayUrl = url;
  try {
    const urlObj = new URL(url);
    hostname = urlObj.hostname;
    displayUrl = url.replace(/^https?:\/\//, "");
  } catch {
    hostname = url;
    displayUrl = url;
  }

  // Format date as yyyy/mm/dd
  const createdDate = new Date(createdAt);
  const formattedDate = createdDate
    .toISOString()
    .split("T")[0]
    .replace(/-/g, "/");

  const getScoreColor = (value: number) => {
    if (value >= 80) return "#10b981"; // green-500
    if (value >= 60) return "#f97316"; // orange-500
    return "#ef4444"; // red-500
  };

  const chartData = [
    { name: "Score", value: score, fill: getScoreColor(score) },
    { name: "Remaining", value: 100 - score, fill: "transparent" },
  ];

  const createMiniChartData = (value: number) => [
    { name: "Score", value, fill: getScoreColor(value) },
    { name: "Remaining", value: 100 - value, fill: "transparent" },
  ];

  return (
    <div
      className={cn(
        "relative flex flex-col items-center justify-start gap-8 rounded-2xl border text-white",
        "border-white/10",
        className
      )}
      style={{
        width,
        height,
        boxSizing: "border-box",
        padding: 48,
        background:
          "radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.06) 0%, transparent 50%), " +
          "radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.035) 0%, transparent 50%), " +
          "radial-gradient(circle at 40% 40%, rgba(255, 255, 255, 0.02) 0%, transparent 50%), " +
          "radial-gradient(circle at 60% 60%, rgba(255, 255,  255, 0.015) 0%, transparent 70%)",
        // Force dark background regardless of page theme
        backgroundColor: "oklch(0.145 0 0)",
        fontFamily:
          'Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, Noto Sans, Helvetica Neue, Arial, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
        ...style,
      }}
    >
      {/* Generator brand */}
      <div className="w-full flex flex-col items-center justify-center gap-2 -mb-1">
        <div className="text-foreground/60 text-2xl">Generated by</div>
        <div className="flex items-center gap-4">
          <Image
            src="/logo.svg"
            alt="YourWebsiteScore logo"
            width={40}
            height={40}
            priority
          />
          <span className="text-3xl font-semibold tracking-tight">
            YourWebsiteScore.com
          </span>
        </div>
      </div>

      {/* Main title */}
      <div className="w-full text-center font-extrabold text-8xl tracking-tight">
        Website Score
      </div>

      {/* Website info under title: favicon + domain */}
      <div className="flex items-center justify-center gap-4 mt-8">
        {hostname && (
          <Image
            src={`/api/favicon?domain=${hostname}&sz=128`}
            alt="Favicon"
            width={54}
            height={54}
            className="!max-w-none"
            unoptimized
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        )}
        <div className="font-extrabold text-4xl tracking-tight">
          {displayUrl || "Website"}
        </div>
      </div>

      {/* Date under domain */}
      <div className="text-center text-foreground/60 text-2xl -mt-8">
        {formattedDate}
      </div>

      {/* Score chart (larger, no animation) */}
      <div className="relative h-96 w-full mb-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={120}
              outerRadius={150}
              strokeWidth={0}
              cornerRadius={14}
              dataKey="value"
              startAngle={90}
              endAngle={-270}
              isAnimationActive={false}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="text-8xl font-black tracking-tight">{score}</div>
        </div>
      </div>

      {/* Title under chart */}
      <div className="-mt-6">
        <h3 className="text-4xl font-bold tracking-tight">Overall Score</h3>
      </div>

      {/* Mini charts row */}
      <div className="w-full grid grid-cols-3 gap-8">
        {/* PageSpeed Mini Donut */}
        <div className="text-center space-y-3">
          <div className="relative h-64 w-full mb-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={createMiniChartData(pageSpeedScore ?? 0)}
                  cx="50%"
                  cy="50%"
                  innerRadius={72}
                  outerRadius={96}
                  strokeWidth={0}
                  cornerRadius={14}
                  dataKey="value"
                  startAngle={90}
                  endAngle={-270}
                  isAnimationActive={false}
                >
                  {createMiniChartData(pageSpeedScore ?? 0).map(
                    (entry, index) => (
                      <Cell key={`cell-ps-${index}`} fill={entry.fill} />
                    )
                  )}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
              <div className="text-4xl font-bold tracking-tight text-white">
                {pageSpeedScore ?? 0}
              </div>
            </div>
          </div>
          <div className="space-y-1">
            <h3 className="text-3xl font-bold tracking-tight">PageSpeed</h3>
          </div>
        </div>

        {/* Website Quality Mini Donut */}
        <div className="text-center space-y-3">
          <div className="relative h-64 w-full mb-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={createMiniChartData(websiteQualityScore ?? 0)}
                  cx="50%"
                  cy="50%"
                  innerRadius={72}
                  outerRadius={96}
                  strokeWidth={0}
                  cornerRadius={14}
                  dataKey="value"
                  startAngle={90}
                  endAngle={-270}
                  isAnimationActive={false}
                >
                  {createMiniChartData(websiteQualityScore ?? 0).map(
                    (entry, index) => (
                      <Cell key={`cell-wq-${index}`} fill={entry.fill} />
                    )
                  )}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
              <div className="text-4xl font-bold tracking-tight text-white">
                {websiteQualityScore ?? 0}
              </div>
            </div>
          </div>
          <div className="space-y-1">
            <h3 className="text-3xl font-bold tracking-tight">
              Website Quality
            </h3>
          </div>
        </div>

        {/* Trust & Security Mini Donut */}
        <div className="text-center space-y-3">
          <div className="relative h-64 w-full mb-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={createMiniChartData(trustSecurityScore ?? 0)}
                  cx="50%"
                  cy="50%"
                  innerRadius={72}
                  outerRadius={96}
                  strokeWidth={0}
                  cornerRadius={14}
                  dataKey="value"
                  startAngle={90}
                  endAngle={-270}
                  isAnimationActive={false}
                >
                  {createMiniChartData(trustSecurityScore ?? 0).map(
                    (entry, index) => (
                      <Cell key={`cell-ts-${index}`} fill={entry.fill} />
                    )
                  )}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
              <div className="text-4xl font-bold tracking-tight text-white">
                {trustSecurityScore ?? 0}
              </div>
            </div>
          </div>
          <div className="space-y-1">
            <h3 className="text-3xl font-bold tracking-tight">
              Trust & Security
            </h3>
          </div>
        </div>
      </div>

      {/**
       * The sections below are intentionally commented out while we restyle step-by-step.
       * They will be re-enabled and refined in subsequent edits.
       */}
      {/** Additional content will be re-enabled in subsequent edits. */}

      {/**
      <div className="mt-2 w-full max-w-md rounded-2xl border border-black/10 bg-white shadow-sm p-8 flex flex-col items-center">
        ... score card ...
      </div>

      <div className="w-full max-w-md grid grid-cols-3 gap-3">
        ... sub scores ...
      </div>

      <div className="mt-auto w-full text-center text-black/50 text-sm">
        Generated with speedtest
      </div>
      */}
      {/* Bottom-right report id */}
      {reportId && (
        <div className="absolute bottom-6 right-6 text-foreground/60 text-xl">
          Report: <span className="font-semibold">{reportId}</span>
        </div>
      )}
    </div>
  );
}
